import { ApplicationRef, ComponentFactory, ComponentFactoryResolver, ComponentRef, Injectable, Injector } from '@angular/core'
import { IDynamicRenderContainer } from '../models/dynamic-render-container'
import { IDynamicRenderControl } from '../models/dynamic-render-control'
import { dynamicRenderControlToken, dynamicRenderPropsToken } from '../tokens'
import { isEmpty } from '@adex/shared/utils/data'
import { Observable } from 'rxjs'
import { takeUntil, tap } from 'rxjs/operators'


@Injectable({
  providedIn: 'root'
})
export class DynamicRenderService {
  private readonly containers: Map<string, IDynamicRenderContainer> = new Map()
  private readonly controls: Map<string, IDynamicRenderControl<any>> = new Map()


  constructor(
    private readonly resolver: ComponentFactoryResolver,
    private readonly injector: Injector,
    private readonly appRef: ApplicationRef
  ) {
  }

  public registerContainer(name: string, container: IDynamicRenderContainer): void {
    if (this.containers.has(name)) throw new Error(`Container ${ name } is already registered`)
    this.containers.set(name, container)
  }

  public unregisterContainer(name: string): void {
    this.containers.delete(name)
  }

  public render<C>(control: IDynamicRenderControl<C>, containerName?: string): void {
    this.innerRender(control, containerName)
  }

  public remove(name: string): void
  public remove<C>(control: IDynamicRenderControl<C>): void
  public remove<C>(value: IDynamicRenderControl<C> | string): void {
    const control = typeof value === 'string' ? this.controls.get(value) : value
    const name = typeof value === 'string' ? value : control.name
    if (!control) throw new Error(`Control ${ name } is not found`)
    if (!control.isOpen) throw new Error('The control has not been rendered yet')
    control.destroy$.next()
    const containerName = control.getContainerName()
    if (!this.containers.has(containerName)) throw new Error(`Container ${ containerName } not found for control ${ name }`)
    const container = this.containers.get(containerName)
    const componentRef = control.getComponentRef()
    const hostView = componentRef.hostView
    this.appRef.detachView(hostView)
    container.removeView(name)
    this.controls.delete(name)
    control.close()
  }

  private innerRender<C>(control: IDynamicRenderControl<C>, containerName: string, tik = 0): void {
    const name = control.name
    const innerContainerName = containerName || control.getContainerName()
    if (!this.containers.has(innerContainerName) && tik < 3) {
      requestAnimationFrame(() => this.innerRender(control, containerName, tik + 1))
      return
    }
    if (this.controls.has(name)) return
    if (control.isOpen) return
    if (!innerContainerName) return
    if (!this.containers.has(innerContainerName)) return
    const container = this.containers.get(innerContainerName)
    const view = this.buildView(control)
    control.setContainerName(innerContainerName)
    container.renderView(control.name, view)
    this.controls.set(control.name, control)
    control.open()
  }

  private buildInjector(control: IDynamicRenderControl<any>): Injector {
    const providers = []
    const parent = control.parentInjector || this.injector
    const injectorProps = control.componentProps?.injectorProps || {}
    providers.push({ provide: dynamicRenderControlToken, useValue: control })
    providers.push({ provide: dynamicRenderPropsToken, useValue: injectorProps })
    return Injector.create({ parent, providers })
  }

  private buildView(control: IDynamicRenderControl<any>): HTMLElement {
    const injector = this.buildInjector(control)
    const componentType = control.componentType
    const componentFactory = this.resolver.resolveComponentFactory(componentType)
    const componentRef = componentFactory.create(injector)
    DynamicRenderService.bindingInputs(control, componentFactory, componentRef)
    DynamicRenderService.bindingOutputs(control, componentFactory, componentRef)
    control.setComponentRef(componentRef)
    const hostView = componentRef.hostView
    this.appRef.attachView(hostView)
    return componentRef.location.nativeElement
  }

  private static bindingInputs(
    control: IDynamicRenderControl<any>,
    componentFactory: ComponentFactory<any>,
    componentRef: ComponentRef<any>
  ) {
    const instance = componentRef.instance
    const inputsData = control?.componentProps?.inputs
    if (isEmpty(inputsData)) return
    const inputs = componentFactory.inputs
    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i]
      const templateName = input.templateName
      const propName = input.propName
      const dataValue = Reflect.get(inputsData, templateName)
      if (dataValue instanceof Observable) {
        dataValue.pipe(
          tap(value => Reflect.set(instance, propName, value)),
          tap(() => componentRef.changeDetectorRef.markForCheck()),
          takeUntil(control.destroy$)
        ).subscribe()
      } else {
        Reflect.set(instance, propName, dataValue)
      }
    }
  }

  private static bindingOutputs(
    control: IDynamicRenderControl<any>,
    componentFactory: ComponentFactory<any>,
    componentRef: ComponentRef<any>
  ) {
    const instance = componentRef.instance
    const outputsData = control?.componentProps?.outputs
    if (isEmpty(outputsData)) return
    const outputs = componentFactory.outputs
    for (let i = 0; i < outputs.length; i++) {
      const output = outputs[i]
      const templateName = output.templateName
      const propName = output.propName
      const emitFn = Reflect.get(outputsData, templateName)
      const outputStream = Reflect.get(instance, propName)
      if (!(outputStream instanceof Observable)) throw new TypeError(`Output ${propName} type is not instance Observable`)
      outputStream.pipe(
        tap(value => emitFn(value)),
        takeUntil(control.destroy$)
      ).subscribe()
    }
  }
}
